import type { GemxQlQueryResponse } from '~/core/gemxql/graphql';
import type { IPathAnalysisFirstLevel } from '../contexts';

import { createNumericRowReader, extractQueryRows } from '~/core/gemxql/helpers';
import { EPageField } from '~/core/gemxql/types';
import { EMetricKey } from '~/types/metric';

const readString = (row: Record<string, unknown>, key: string): string => {
  const value = row[key];
  return typeof value === 'string' ? value : '';
};

/**
 * Maps GemxQL campaign-path rows to the legacy `IPathAnalysisFirstLevel[]` shape consumed by
 * `PathAnalysisProvider`. Preserves the legacy mapper's semantics: dedupe by `locationPath`,
 * id `${locationPath}-${index}`, `parentID: ''`, `level: 1`, and — critically — an empty result
 * maps to `undefined` (not `[]`), matching legacy `allPaths.length > 0 ? allPaths : undefined`.
 * `usePathAnalyticsMerger` keys level-1 entries by `locationPath`, so the id scheme is preserved
 * for downstream safety but is not itself load-bearing for the merge.
 */
export const extractCampaignPathsData = (
  response?: GemxQlQueryResponse,
): IPathAnalysisFirstLevel[] | undefined => {
  const rows = extractQueryRows(response);
  if (!rows.length) return undefined;

  const firstLevelPaths: IPathAnalysisFirstLevel[] = [];
  const seenPaths = new Set<string>();

  rows.forEach((row) => {
    const locationPath = readString(row, EPageField.PAGE_PATH);
    if (!locationPath || seenPaths.has(locationPath)) return;
    seenPaths.add(locationPath);

    firstLevelPaths.push({
      id: `${locationPath}-${firstLevelPaths.length}`,
      locationPath,
      pageTitle: readString(row, EPageField.PAGE_TITLE),
      pageType: readString(row, EPageField.PAGE_TYPE),
      parentID: '',
      level: 1,
      metric: {
        totalSessions: createNumericRowReader(row)(EMetricKey.SESSION),
      },
    });
  });

  return firstLevelPaths.length > 0 ? firstLevelPaths : undefined;
};
